/**
 * Fleet-scale signage endpoints — types from `@navio/nsdk`.
 *
 * Bulk commands fan out via Heimdall (canary_pct / pause_on_failure_rate).
 * Per-device Grafana catalog / kiosk pin live on the RPi (Keel) and are
 * exposed via staff proxy routes in `getDeviceUrls` — not a fleet-wide
 * backend Grafana integration.
 */

import type {
  SignageDeviceBulkAssignApiData,
  SignageDeviceBulkAssignApiResponses,
  SignageDeviceBulkCommandApiData,
  SignageDeviceBulkCommandApiResponses,
  SignageDeviceFleetStatsApiData,
  SignageDeviceFleetStatsApiResponses,
  SignageDeviceReleasesApiData,
  SignageDeviceReleasesApiResponses,
  SignageFleetCommandLogListApiData,
  SignageFleetCommandLogListApiResponses,
  SignageGroupAddMembersApiData,
  SignageGroupAddMembersApiResponses,
  SignageGroupCreateApiData,
  SignageGroupCreateApiResponses,
  SignageGroupListApiData,
  SignageGroupListApiResponses,
  SignageRolloutCancelApiData,
  SignageRolloutCancelApiResponses,
  SignageRolloutCreateApiData,
  SignageRolloutCreateApiResponses,
  SignageRolloutListApiData,
  SignageRolloutListApiResponses,
  SignageRolloutPromoteApiData,
  SignageRolloutPromoteApiResponses,
  SignageRolloutStartCanaryApiData,
  SignageRolloutStartCanaryApiResponses
} from '@navio/nsdk';

import { apiUrl, dataEndpoint, responseEndpoint } from '../defineResource';

export const getSignageFleetUrls = {
  getSignageFleetStats: dataEndpoint<SignageDeviceFleetStatsApiData, SignageDeviceFleetStatsApiResponses>({
    getUrl: (queryParams) => apiUrl('devices/signage/stats/', queryParams)
  }),
  getSignageReleases: dataEndpoint<SignageDeviceReleasesApiData, SignageDeviceReleasesApiResponses>({
    getUrl: () => apiUrl('devices/signage/releases/')
  }),
  getSignageFleetCommandLogs: dataEndpoint<SignageFleetCommandLogListApiData, SignageFleetCommandLogListApiResponses>({
    getUrl: (queryParams) => apiUrl('devices/signage/command-logs/', queryParams)
  }),
  getSignageGroups: dataEndpoint<SignageGroupListApiData, SignageGroupListApiResponses>({
    getUrl: (queryParams) => apiUrl('devices/signage/groups/', queryParams)
  }),
  getSignageRollouts: dataEndpoint<SignageRolloutListApiData, SignageRolloutListApiResponses>({
    getUrl: (queryParams) => apiUrl('devices/signage/rollouts/', queryParams)
  }),
  postSignageDeviceBulkCommand: responseEndpoint<SignageDeviceBulkCommandApiData, SignageDeviceBulkCommandApiResponses>(
    {
      getUrl: () => apiUrl('devices/signage/commands/bulk/'),
      getBody: (data) => data
    }
  ),
  postSignageDeviceBulkAssign: responseEndpoint<SignageDeviceBulkAssignApiData, SignageDeviceBulkAssignApiResponses>({
    getUrl: () => apiUrl('devices/signage/bulk-assign/'),
    getBody: (data) => data
  }),
  postSignageGroup: responseEndpoint<SignageGroupCreateApiData, SignageGroupCreateApiResponses>({
    getUrl: () => apiUrl('devices/signage/groups/'),
    getBody: (data) => data
  }),
  postSignageGroupMembers: responseEndpoint<SignageGroupAddMembersApiData, SignageGroupAddMembersApiResponses>({
    getUrl: ({ pk }) => apiUrl(`devices/signage/groups/${pk}/members/`),
    getBody: ({ pk: _, ...body }) => body
  }),
  postSignageRollout: responseEndpoint<SignageRolloutCreateApiData, SignageRolloutCreateApiResponses>({
    getUrl: () => apiUrl('devices/signage/rollouts/'),
    getBody: (data) => data
  }),
  postSignageRolloutCanary: responseEndpoint<SignageRolloutStartCanaryApiData, SignageRolloutStartCanaryApiResponses>({
    getUrl: ({ pk }) => apiUrl(`devices/signage/rollouts/${pk}/canary/`)
  }),
  postSignageRolloutPromote: responseEndpoint<SignageRolloutPromoteApiData, SignageRolloutPromoteApiResponses>({
    getUrl: ({ pk }) => apiUrl(`devices/signage/rollouts/${pk}/promote/`)
  }),
  postSignageRolloutCancel: responseEndpoint<SignageRolloutCancelApiData, SignageRolloutCancelApiResponses>({
    getUrl: ({ pk }) => apiUrl(`devices/signage/rollouts/${pk}/cancel/`)
  })
};
